import { useEffect, useId, useRef } from 'react';
import { SLUG_MAX_LENGTH, normalizeSlugInput, randomSlug } from '@photo-merge/shared';
import type { SlugStatus } from '../hooks/useSlugAvailability';
import styles from './SlugInput.module.css';

interface SlugInputProps {
  value: string;
  onChange: (value: string) => void;
  status: SlugStatus;
  label?: string;
  /** Selects the prefilled value on mount so the first keystroke replaces it. */
  autoSelect?: boolean;
}

function StatusLine({ status, onUseSuggestion }: { status: SlugStatus; onUseSuggestion: (s: string) => void }) {
  switch (status.kind) {
    case 'empty':
      return <span>Pick something you&apos;ll remember.</span>;
    case 'checking':
      return (
        <>
          <span className={styles.spinner} aria-hidden="true" />
          <span>Checking…</span>
        </>
      );
    case 'available':
      return <span className={styles.statusOk}>✓ That link is free</span>;
    case 'taken':
      return (
        <>
          <span className={styles.statusBad}>Already taken</span>
          {status.suggestion ? (
            <button
              type="button"
              className={styles.suggestion}
              onClick={() => onUseSuggestion(status.suggestion!)}
            >
              try {status.suggestion}
            </button>
          ) : null}
        </>
      );
    case 'invalid':
      return <span className={styles.statusBad}>{status.message}</span>;
    case 'error':
      return <span className={styles.statusBad}>{status.message}</span>;
  }
}

export function SlugInput({
  value,
  onChange,
  status,
  label = 'Your game link',
  autoSelect = false,
}: SlugInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  useEffect(() => {
    if (!autoSelect) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
    // iOS Safari won't raise the keyboard without a user gesture, but the selection
    // still shows — so tapping the field types over the suggestion, as intended.
  }, [autoSelect]);

  const tone =
    status.kind === 'available'
      ? styles.ok
      : status.kind === 'taken' || status.kind === 'invalid'
        ? styles.bad
        : '';

  return (
    <div>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <div className={[styles.field, tone].filter(Boolean).join(' ')}>
        <span className={styles.prefix} aria-hidden="true">
          /play/
        </span>
        <input
          ref={inputRef}
          id={id}
          className={styles.input}
          value={value}
          onChange={(e) => onChange(normalizeSlugInput(e.target.value))}
          placeholder="grandmas-80th"
          inputMode="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={SLUG_MAX_LENGTH}
          enterKeyHint="go"
        />
        <button
          type="button"
          className={styles.dice}
          onClick={() => {
            onChange(randomSlug());
            inputRef.current?.focus();
          }}
          aria-label="Suggest another link"
          title="Suggest another"
        >
          🎲
        </button>
      </div>
      <p className={styles.status} role="status" aria-live="polite">
        <StatusLine status={status} onUseSuggestion={onChange} />
      </p>
    </div>
  );
}
